import { useFragment } from "react-relay"
import { t } from "i18next"
import { Tag } from "@/components/ui/tag"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { joinStrings } from "@/lib/joinString"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { AllergiesDrawer } from "@/modules/patients/detail/health-information/allergy/allergies-drawer"
import { AllergyCopilots } from "@/modules/patients/detail/health-information/allergy/allergy-copilots"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import {
  PatientAllergiesOutputFragment$data,
  PatientAllergiesOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { usePatientAllergies } from "@/modules/patients/services/usePatientAllergies"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

interface AllergyBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}
interface AllergyTagProps {
  code: string
  text: string
  comment: string | null
  testId: string
}

const AllergyTag = ({ code, comment, text, testId }: AllergyTagProps) => {
  return (
    <TooltipCustom>
      <TooltipTrigger className="flex max-w-full flex-wrap" onClick={(e) => e.preventDefault()}>
        <Tag
          className="truncate"
          key={code}
          type="neutral"
          label={comment && comment.trim().length > 0 ? comment : text}
          visibleTitle={false}
          data-testid={testId}
        />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent
          sideOffset={7}
          align="start"
          side="bottom"
          onPointerDownOutside={(e) => e.preventDefault()}
          className="bg-surface-inverted flex flex-col rounded-sm px-1 py-0.5 text-xs tracking-[0.25px]">
          {joinStrings([code, text], " - ")}
          <span>{comment}</span>
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

export const Allergy = ({ country, patientId }: AllergyBodyProps) => {
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const dataRef = usePatientAllergies(patientId, country)
  const data = useFragment<PatientAllergiesOutputFragment$key>(PatientAllergiesOutputFragment, dataRef)
  const { verifiedBy, verifiedAt, isVerified } = useGetHealthInformationVerification(patientId, country, "ALLERGY")

  const validAppointment = useHealthInformationInContextAppointment()
  const canVerify = !isVerified && validAppointment

  const handleOpenAllergyDrawer = () => {
    openDrawer(
      () => <AllergiesDrawer canVerify={canVerify} verifiedBy={verifiedBy ?? ""} verifiedAt={verifiedAt ?? ""} />,
      "AllergiesDrawer",
    )
  }

  return (
    <HealthDataCardSession
      canVerify={canVerify}
      sectionName="allergy"
      analyticsSectionName="healthInformation_allergy"
      disabled={drawerName === "AllergiesDrawer"}
      title={t("patient.healthInformation.allergies.title")}
      editHandler={handleOpenAllergyDrawer}
      bodyContentClassName="grid grid-cols-1 grid-rows-1 gap-4">
      <AllergyCopilots
        onOpenAllergyDrawer={handleOpenAllergyDrawer}
        patientId={patientId}
        currentAllergies={data.allergies}
      />
      <AllergyCardBody data={data} />
    </HealthDataCardSession>
  )
}

const AllergyCardBody = ({ data }: { data: PatientAllergiesOutputFragment$data }) => {
  switch (data.status) {
    case "SOME":
      return (
        <div className="flex max-w-full flex-wrap items-center gap-2" data-testid="allergy-tag-section">
          {data.allergies.map((allergy, index) => {
            return (
              <AllergyTag
                key={`${allergy.icdDiagnosis.diagnosisCode}-${index + 1}`}
                code={allergy.icdDiagnosis.diagnosisCode}
                text={allergy.icdDiagnosis.text}
                comment={allergy.comment ?? allergy.icdDiagnosis.text}
                testId="allergy-tag-item"
              />
            )
          })}
        </div>
      )
    case "NONE":
      return (
        <span
          className="text-on-surface-subdued min-h-5 px-1.5 py-1 text-xs leading-4 font-normal tracking-[0.25px]"
          data-testid="allergy-none-tag">
          {t("patient.healthInformation.none")}
        </span>
      )
    case "UNKNOWN":
      return null
  }
}
